// Clientes (PF/PJ) do usuário — fonte de verdade: tabela `clientes` no
// Supabase. Padrão do app: hidratado em memória no login (hydrateAll) com
// getters síncronos e escrita write-through assíncrona.
import { supabase } from "@/integrations/supabase/client";

export type ClienteTipo = "PF" | "PJ";

export interface ClienteEndereco {
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
  cep?: string;
}

export interface ClienteContato {
  id: string;
  nome: string;
  telefone?: string;
  email?: string;
}

export interface Cliente {
  id: string;
  tipo: ClienteTipo;
  nome?: string; // PF
  nomeFantasia?: string; // PJ
  razaoSocial?: string; // PJ
  cpf?: string;
  cnpj?: string;
  telefones: string[];
  emails: string[];
  endereco: ClienteEndereco;
  contatos?: ClienteContato[];
  observacoes?: string;
  createdAt: string;
  updatedAt: string;
}

let _clientes: Cliente[] = [];

function rowToCliente(row: any): Cliente {
  return {
    id: row.id,
    tipo: (row.tipo as ClienteTipo) ?? "PF",
    nome: row.nome ?? undefined,
    nomeFantasia: row.nome_fantasia ?? undefined,
    razaoSocial: row.razao_social ?? undefined,
    cpf: row.cpf ?? undefined,
    cnpj: row.cnpj ?? undefined,
    telefones: (row.telefones as string[]) ?? [],
    emails: (row.emails as string[]) ?? [],
    endereco: (row.endereco as ClienteEndereco) ?? {},
    contatos: (row.contatos as ClienteContato[]) ?? [],
    observacoes: row.observacoes ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function clienteToRow(c: Omit<Cliente, "id" | "createdAt" | "updatedAt">) {
  return {
    tipo: c.tipo,
    nome: c.nome ?? null,
    nome_fantasia: c.nomeFantasia ?? null,
    razao_social: c.razaoSocial ?? null,
    cpf: c.cpf ?? null,
    cnpj: c.cnpj ?? null,
    telefones: (c.telefones ?? []) as any,
    emails: (c.emails ?? []) as any,
    endereco: (c.endereco ?? {}) as any,
    contatos: (c.contatos ?? []) as any,
    observacoes: c.observacoes ?? null,
  };
}

async function currentUserId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

export class ClienteCache {
  static async hydrate(): Promise<void> {
    const { data, error } = await supabase
      .from("clientes")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) { console.error("ClienteCache.hydrate:", error.message); return; }
    _clientes = (data ?? []).map(rowToCliente);
  }

  static getClientes(): Cliente[] {
    return _clientes;
  }

  static getClienteById(id: string): Cliente | undefined {
    return _clientes.find((c) => c.id === id);
  }

  static addCliente(cliente: Omit<Cliente, "id" | "createdAt" | "updatedAt">): Cliente {
    const now = new Date().toISOString();
    const temp: Cliente = { ...cliente, id: `tmp-${Date.now()}`, createdAt: now, updatedAt: now };
    _clientes = [temp, ..._clientes];
    (async () => {
      const userId = await currentUserId();
      if (!userId) return;
      const { data, error } = await supabase
        .from("clientes")
        .insert({ user_id: userId, ...clienteToRow(cliente) })
        .select("*")
        .single();
      if (error) { console.error("ClienteCache.addCliente:", error.message); return; }
      _clientes = _clientes.map((c) => (c.id === temp.id ? rowToCliente(data) : c));
    })();
    return temp;
  }

  static updateCliente(updated: Cliente): void {
    _clientes = _clientes.map((c) =>
      c.id === updated.id ? { ...updated, updatedAt: new Date().toISOString() } : c
    );
    (async () => {
      const { error } = await supabase
        .from("clientes")
        .update(clienteToRow(updated))
        .eq("id", updated.id);
      if (error) console.error("ClienteCache.updateCliente:", error.message);
    })();
  }

  static deleteCliente(id: string): void {
    _clientes = _clientes.filter((c) => c.id !== id);
    (async () => {
      const { error } = await supabase.from("clientes").delete().eq("id", id);
      if (error) console.error("ClienteCache.deleteCliente:", error.message);
    })();
  }
}

// Nome de exibição de um cliente (PF: nome; PJ: fantasia/razão social).
export function clienteDisplayName(c: Cliente): string {
  return (c.tipo === "PJ" ? c.nomeFantasia || c.razaoSocial : c.nome) || "Cliente sem nome";
}
